// ============================================================
// syncLock.ts — ユーザー単位の同期ロック（クライアント側・Firestore web SDK）。
//
//   目的: クライアント同期（taskSync.syncMyTasksWithGoogle・ポータルを開くと走る）と
//   サーバー同期（serverSync.syncUserTasks・10分毎の scheduledSync）が **同じユーザーの
//   タスクを同時に同期** すると、未同期タスクが二重に Google 作成される（重複）。これを
//   `syncLocks/{uid}` の **アトミックなロック** で防ぐ。
//
//   設計:
//   - `syncLocks/{uid}` = { lockedAt: number(ms) }。
//   - acquire: runTransaction 内で read → 「存在しない or now - lockedAt >= TTL(5分)」なら
//     set({lockedAt: now}) して true／それ以外（有効なロックが進行中）は false。
//     read-then-write を1トランザクションに閉じ込めるので、2者が同時に acquire しても
//     どちらか一方だけが true を得る（レースにならない）。
//   - release: delete。クラッシュ・例外で release されなくても TTL 5分で自然解放される
//     （acquire 側が stale ロックを奪える）ので永久ブロックしない。
//
//   ⚠️ db（web SDK）が null（DEV/未設定）ならロックを **スキップ** して従来動作に倒す
//      （DEV は Firestore 無しで動くため。実害なし）。サーバー側は firebase-admin で同名の
//      ロジックを serverSync 側に実装し、クライアントとサーバーが同じ `syncLocks/{uid}` を
//      尊重して排他する。
// ============================================================
import { deleteDoc, doc, runTransaction } from 'firebase/firestore';
import { db } from './firebase';

/** stale ロックとみなす閾値（ms）。この時間を超えたロックは acquire 側が奪える。 */
export const SYNC_LOCK_TTL_MS = 5 * 60 * 1000; // 5分

/**
 * uid のロックをアトミックに取得する。
 *   取得できた（＝自分が同期してよい）→ true / 別の同期が進行中 → false。
 *   db が null（DEV）なら true を返す（ロック無しで従来動作・実害なし）。
 * トランザクション自体が失敗した場合は false（安全側＝同期をスキップ）。
 */
export async function acquireSyncLock(uid: string): Promise<boolean> {
  // DEV（Firestore 無し）はロック機構が存在しないので素通し（従来動作・実害なし）。
  if (!db) return true;
  // 🔴 2026-07-26（対策④）: 本番で uid が未確定のときは **同期しない**。
  //    旧実装は `!db || !uid` で true を返していたため、uid が空だとロックを取らずに
  //    同期本体が走り、サーバー同期（scheduledSync）と同時実行されうる＝同じタスクを
  //    双方が Google に作る（重複）。ロックを取れないなら同期を見送る方が安全。
  if (!uid) {
    console.warn('[syncLock] uid が無いため同期をスキップします');
    return false;
  }
  try {
    return await runTransaction(db, async (tx) => {
      const ref = doc(db!, 'syncLocks', uid);
      const snap = await tx.get(ref);
      const now = Date.now();
      if (snap.exists()) {
        const lockedAt = (snap.data() as { lockedAt?: number }).lockedAt ?? 0;
        // 有効なロックが進行中（TTL 未経過）→ 取得失敗。
        if (now - lockedAt < SYNC_LOCK_TTL_MS) return false;
        // stale（TTL 経過）→ 奪って上書き。
      }
      tx.set(ref, { lockedAt: now });
      return true;
    });
  } catch (err) {
    // トランザクション失敗（競合リトライ上限・権限等）は安全側で「取れなかった」に倒す。
    console.warn('[syncLock] acquire 例外（スキップ扱い）', err instanceof Error ? err.message : err);
    return false;
  }
}

/**
 * uid のロックを解放する（delete）。取得に成功したときだけ finally で必ず呼ぶ。
 *   db が null（DEV）は no-op。失敗しても TTL 5分で自然解放されるので握りつぶす。
 */
export async function releaseSyncLock(uid: string): Promise<void> {
  if (!db || !uid) return;
  try {
    await deleteDoc(doc(db, 'syncLocks', uid));
  } catch (err) {
    // 解放失敗は致命的でない（TTL で自然解放）。秘密は出さない。
    console.warn('[syncLock] release 例外（TTLで自然解放）', err instanceof Error ? err.message : err);
  }
}
